import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useSEO from '../hooks/useSEO';
import { getStoredRef } from '../utils/referral';
import { getStoredAttribution } from '../utils/attribution';
import { track } from '../utils/track';

// Only same-site paths for ?next — "//evil.com" would be an open redirect.
const safeNext = (v) => (typeof v === 'string' && v.startsWith('/') && !v.startsWith('//') ? v : null);

export default function RegisterPage() {
  useSEO({ title: 'Create Account | Steady Education', description: 'Create your Steady Education account to start state-approved real estate continuing education online in Arizona or Kansas.', url: 'https://steadyeducation.com/register' });
  const { register } = useAuth();
  const navigate = useNavigate();
  // ?next=/cart when a guest pressed Checkout: come back to the (saved) cart after signup.
  const next = safeNext(new URLSearchParams(useLocation().search).get('next'));
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '', phone: '', licenseNumber: '', brokerage: '', website: '', homepage: '', bio: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Timestamp when the form actually mounted for a real user. Set in useEffect so
  // it stays null during build-time prerender (avoids hydration mismatch).
  const formLoadedAt = useRef(null);
  useEffect(() => { formLoadedAt.current = Date.now(); }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      await register({ ...form, referralCode: getStoredRef() || undefined, attribution: getStoredAttribution() || undefined, formLoadedAt: formLoadedAt.current });
      track('register');
      navigate(next || '/my-courses');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <Link to="/" style={styles.logoLink}>
          <div style={styles.logoMark}>SE</div>
          <div>
            <div style={styles.logoName}>Steady Education</div>
            <div style={styles.logoSub}>Arizona Real Estate CE</div>
          </div>
        </Link>

        <h1 style={styles.title}>Create your account</h1>
        <p style={styles.sub}>Start your Arizona CE courses today</p>

        {next === '/cart' && (
          <div className="alert" style={{ background: 'var(--forest-50, #eef5f1)', border: '1px solid var(--forest-200, #cfe3d8)', color: 'var(--forest-700, #1d4142)' }}>
            Create your free account to check out — your cart is saved.
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Three honeypots with different hiding strategies. A bot that skips one
              variant usually doesn't skip all three. Any of them filled → server blocks. */}
          <div aria-hidden="true" style={styles.honeypot}>
            <label htmlFor="website-hp">Website (leave this field blank)</label>
            <input id="website-hp" type="text" name="website" tabIndex="-1" autoComplete="off"
              value={form.website} onChange={set('website')} />
          </div>
          <div aria-hidden="true" style={styles.honeypotSr}>
            <label htmlFor="homepage-hp">Homepage URL</label>
            <input id="homepage-hp" type="text" name="homepage" tabIndex="-1" autoComplete="off"
              value={form.homepage} onChange={set('homepage')} />
          </div>
          <div aria-hidden="true" style={styles.honeypotZero}>
            <label htmlFor="bio-hp">Bio</label>
            <textarea id="bio-hp" name="bio" tabIndex="-1" autoComplete="off"
              value={form.bio} onChange={set('bio')} />
          </div>
          <div style={styles.row}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">First Name *</label>
              <input className="input" placeholder="Jane" value={form.firstName} onChange={set('firstName')} required />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Last Name *</label>
              <input className="input" placeholder="Smith" value={form.lastName} onChange={set('lastName')} required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input className="input" type="email" placeholder="you@example.com" value={form.email} onChange={set('email')} required />
          </div>
          <div className="form-group">
            <label className="form-label">Password *</label>
            <input className="input" type="password" placeholder="Min. 8 characters" value={form.password} onChange={set('password')} required />
          </div>
          <div style={styles.row}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Phone</label>
              <input className="input" placeholder="(480) 555-0100" value={form.phone} onChange={set('phone')} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">ADRE License #</label>
              <input className="input" placeholder="SA12345678" value={form.licenseNumber} onChange={set('licenseNumber')} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Arizona Brokerage</label>
            <input className="input" placeholder="Your brokerage name" value={form.brokerage} onChange={set('brokerage')} />
          </div>
          <button className="btn btn-primary btn-lg" type="submit" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p style={styles.terms}>
          By creating an account you agree to our <Link to="/terms">Terms of Service</Link>.
        </p>
        <p style={styles.loginLink}>
          Already have an account? <Link to={next ? `/login?next=${encodeURIComponent(next)}` : '/login'}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  root: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--slate-50)', padding: 20 },
  card: { background: 'white', border: '1px solid var(--color-border)', borderRadius: 20, padding: '40px', width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 18, boxShadow: 'var(--shadow-lg)' },
  logoLink: { display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' },
  logoMark: { width: 42, height: 42, background: 'linear-gradient(135deg, #0d9488, #0f766e)', borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 15 },
  logoName: { fontSize: 16, fontWeight: 700, color: 'var(--slate-900)' },
  logoSub: { fontSize: 11, color: 'var(--color-text-muted)' },
  title: { fontFamily: 'var(--font-display)', fontSize: 26, color: 'var(--slate-900)' },
  sub: { fontSize: 15, color: 'var(--color-text-muted)', marginTop: -10 },
  form: { display: 'flex', flexDirection: 'column', gap: 14 },
  row: { display: 'flex', gap: 12 },
  terms: { fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center' },
  loginLink: { fontSize: 14, color: 'var(--color-text-muted)', textAlign: 'center' },
  // Honeypot variant 1: off-screen with 1px dimensions.
  honeypot: {
    position: 'absolute',
    left: '-9999px',
    top: 'auto',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
  },
  // Honeypot variant 2: sr-only-style clip trick used for accessibility helpers,
  // familiar to some bots but often skipped in visibility checks.
  honeypotSr: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  // Honeypot variant 3: rendered inline at zero height with opacity 0.
  // Human eye sees nothing; some bots skip only 'display:none' or 'visibility:hidden'.
  honeypotZero: {
    height: 0,
    opacity: 0,
    pointerEvents: 'none',
    overflow: 'hidden',
  },
};
